// Beta Feedback form — localised copy (Stage 4 — 2026-02).
//
// Coverage rules:
//  • English & Portuguese: user-authored copy from the product spec.
//  • Spanish (es-419): FORMAL — always uses `usted` / `su` / `sus`.
//  • French (fr-CA): FORMAL — always uses `vous` / `votre` / `vos`.
//
// The keys mirror the schema in `./schema.js`. `choiceLabels` covers
// every value in every question's `choices[]`. `promptHint` /
// `promptStrong` are the placeholder copy for the free-text
// comment field (strong prompt fires for `negative` choices).

const EN = {
  pageTitle: "Beta feedback",
  headerSubtitle: "Help us make My English Coach better before public launch.",
  progress: (done, total) => `${done} of ${total} sections complete`,
  submit: "Submit feedback",
  submitting: "Submitting…",
  back: "Back to results",
  draftSaved: "Draft saved on this device.",
  draftRestored: "We restored your saved draft.",
  incompleteToast: "Please answer every required question before submitting.",
  submitSuccessToast: "Thanks for your feedback!",
  submitErrorToast: "Something went wrong. Please try again.",
  notEligibleToast: "You're not eligible to submit feedback for this session.",
  alreadySubmittedToast: "You've already submitted feedback for this session.",
  optionalLabel: "Optional",
  requiredLabel: "Required",
  commentLabel: "Comment",
  commentRequiredLabel: "Comment required",
  commentRequiredHelper: "Please explain your response so we can improve.",
  commentRequiredError: "Please add a comment before submitting.",
  chooseAria: "Choose an option",

  sectionTitles: {
    overall_experience: "Section 1 — Overall Experience",
    interview_questions: "Section 2 — Interview Questions",
    assessment_report: "Section 3 — Assessment Report",
    suggested_improved_answers: "Section 4 — Suggested Improved Answers",
    study_guide: "Section 5 — Study Guide",
    re_practice: "Section 6 — Re-Practice",
    ease_of_use: "Section 7 — Ease of Use",
    final_feedback: "Section 8 — Final Feedback",
  },

  questions: {
    oe_overall_rating: "How would you rate your overall experience with the Interview Simulator?",
    oe_best_part: "What was the best part of your experience?",
    oe_frustrating: "What was the most frustrating or confusing part?",
    iq_relevance: "How relevant were the interview questions to your résumé and job description?",
    iq_realistic: "How realistic did the questions feel compared with a real job interview?",
    iq_hard_wording: "Were any questions difficult to understand because of the English wording?",
    iq_repetitive: "Did any questions feel repetitive or too similar?",
    ar_fair: "How fair did the scores feel based on the answers you gave?",
    ar_useful: "How useful was the question-by-question feedback?",
    ar_clarity: "Did the feedback clearly show what you did well and what you should improve?",
    ar_incorrect: "Did any feedback seem incorrect or based on something you did not actually say?",
    sia_useful: "How useful were the suggested improved answers?",
    sia_fabricated: "Did any suggested answer include experience, responsibilities, achievements, or facts that were not true for you?",
    sg_reviewed: "Did you review the Study Guide?",
    sg_useful: "How useful was the Study Guide?",
    sg_seven_day_plan: "Did the 7-day plan give you clear and practical activities to work on?",
    rp_used: "Did you use the Re-Practice feature?",
    rp_helped: "Did repeating the same questions help you apply the feedback from your first interview?",
    rp_feedback: "What feedback do you have about your Re-Practice experience?",
    rp_why_not: "What made you decide not to use Re-Practice?",
    eu_ease: "How easy was it to complete the full process — from résumé/job description upload through reviewing your results?",
    eu_issues: "Did anything not work as expected?",
    ff_use_again: "Would you use this tool again before a real job interview?",
    ff_recommend: "Would you recommend it to someone preparing for an English-language job interview?",
    ff_improve: "What is the one thing you would most like us to improve before launch?",
    ff_anything_else: "Anything else you would like us to know?",
  },

  choiceLabels: {
    excellent: "Excellent", very_good: "Very good", good: "Good", fair: "Fair", poor: "Poor",
    very_relevant: "Very relevant", mostly_relevant: "Mostly relevant", somewhat_relevant: "Somewhat relevant", not_very_relevant: "Not very relevant", not_relevant: "Not relevant",
    very_realistic: "Very realistic", mostly_realistic: "Mostly realistic", somewhat_realistic: "Somewhat realistic", not_very_realistic: "Not very realistic", not_realistic: "Not realistic",
    yes: "Yes", no: "No", unsure: "Unsure",
    very_fair: "Very fair", mostly_fair: "Mostly fair", somewhat_fair: "Somewhat fair", not_very_fair: "Not very fair", not_fair: "Not fair",
    very_useful: "Very useful", mostly_useful: "Mostly useful", somewhat_useful: "Somewhat useful", not_very_useful: "Not very useful", not_useful: "Not useful",
    yes_very_clearly: "Yes, very clearly", mostly: "Mostly", somewhat: "Somewhat", not_really: "Not really",
    yes_definitely: "Yes, definitely",
    yes_significantly: "Yes, significantly", yes_somewhat: "Yes, somewhat", a_little: "A little",
    very_easy: "Very easy", mostly_easy: "Mostly easy", somewhat_difficult: "Somewhat difficult", very_difficult: "Very difficult",
    definitely: "Definitely", probably: "Probably", maybe: "Maybe", probably_not: "Probably not", definitely_not: "Definitely not",
  },

  promptHint: "Add a comment (optional).",
  promptStrong: "Please tell us more — which question(s) and why.",
  promptStrongFabricated: "Please tell us which question and what was added.",
  promptStrongIssues: "Please describe what happened.",
  promptStrongWhyNot: "Tell us what stopped you from using it.",
};

const PT = {
  pageTitle: "Feedback beta",
  headerSubtitle: "Ajude-nos a melhorar o My English Coach antes do lançamento público.",
  progress: (done, total) => `${done} de ${total} seções concluídas`,
  submit: "Enviar feedback",
  submitting: "Enviando…",
  back: "Voltar aos resultados",
  draftSaved: "Rascunho salvo neste dispositivo.",
  draftRestored: "Restauramos seu rascunho salvo.",
  incompleteToast: "Responda a todas as perguntas obrigatórias antes de enviar.",
  submitSuccessToast: "Obrigado pelo seu feedback!",
  submitErrorToast: "Algo deu errado. Tente novamente.",
  notEligibleToast: "Você não pode enviar feedback para esta sessão.",
  alreadySubmittedToast: "Você já enviou feedback para esta sessão.",
  optionalLabel: "Opcional",
  requiredLabel: "Obrigatória",
  commentLabel: "Comentário",
  commentRequiredLabel: "Comentário obrigatório",
  commentRequiredHelper: "Explique sua resposta para nos ajudar a melhorar.",
  commentRequiredError: "Adicione um comentário antes de enviar.",
  chooseAria: "Escolha uma opção",

  sectionTitles: {
    overall_experience: "Seção 1 — Experiência geral",
    interview_questions: "Seção 2 — Perguntas da entrevista",
    assessment_report: "Seção 3 — Relatório de avaliação",
    suggested_improved_answers: "Seção 4 — Respostas aperfeiçoadas sugeridas",
    study_guide: "Seção 5 — Guia de estudo",
    re_practice: "Seção 6 — Repraticar",
    ease_of_use: "Seção 7 — Facilidade de uso",
    final_feedback: "Seção 8 — Feedback final",
  },

  questions: {
    oe_overall_rating: "Como você avalia sua experiência geral com o Simulador de Entrevistas?",
    oe_best_part: "Qual foi a melhor parte da sua experiência?",
    oe_frustrating: "Qual foi a parte mais frustrante ou confusa?",
    iq_relevance: "Quão relevantes foram as perguntas em relação ao seu currículo e à descrição da vaga?",
    iq_realistic: "Quão realistas as perguntas pareceram em comparação com uma entrevista real?",
    iq_hard_wording: "Alguma pergunta ficou difícil de entender por causa do inglês usado?",
    iq_repetitive: "Alguma pergunta pareceu repetitiva ou muito parecida com outra?",
    ar_fair: "Quão justas as pontuações pareceram considerando as respostas que você deu?",
    ar_useful: "Quão útil foi o feedback pergunta por pergunta?",
    ar_clarity: "O feedback mostrou claramente o que você fez bem e o que deve melhorar?",
    ar_incorrect: "Algum feedback pareceu incorreto ou baseado em algo que você não disse?",
    sia_useful: "Quão úteis foram as respostas aperfeiçoadas sugeridas?",
    sia_fabricated: "Alguma resposta sugerida incluiu experiências, responsabilidades, conquistas ou fatos que não eram verdadeiros sobre você?",
    sg_reviewed: "Você revisou o Guia de Estudo?",
    sg_useful: "Quão útil foi o Guia de Estudo?",
    sg_seven_day_plan: "O plano de 7 dias trouxe atividades claras e práticas para você trabalhar?",
    rp_used: "Você usou o recurso Repraticar?",
    rp_helped: "Repetir as mesmas perguntas ajudou você a aplicar o feedback da primeira entrevista?",
    rp_feedback: "Que feedback você tem sobre sua experiência com o Repraticar?",
    rp_why_not: "O que fez você decidir não usar o Repraticar?",
    eu_ease: "Quão fácil foi concluir todo o processo — do envio do currículo/descrição da vaga até a revisão dos resultados?",
    eu_issues: "Algo não funcionou como esperado?",
    ff_use_again: "Você usaria esta ferramenta de novo antes de uma entrevista real?",
    ff_recommend: "Você recomendaria a alguém se preparando para uma entrevista em inglês?",
    ff_improve: "Qual é a coisa que você mais gostaria que a gente melhorasse antes do lançamento?",
    ff_anything_else: "Alguma outra coisa que você gostaria de nos dizer?",
  },

  choiceLabels: {
    excellent: "Excelente", very_good: "Muito boa", good: "Boa", fair: "Regular", poor: "Ruim",
    very_relevant: "Muito relevantes", mostly_relevant: "Principalmente relevantes", somewhat_relevant: "Um pouco relevantes", not_very_relevant: "Pouco relevantes", not_relevant: "Nada relevantes",
    very_realistic: "Muito realistas", mostly_realistic: "Bastante realistas", somewhat_realistic: "Um pouco realistas", not_very_realistic: "Pouco realistas", not_realistic: "Nada realistas",
    yes: "Sim", no: "Não", unsure: "Não tenho certeza",
    very_fair: "Muito justas", mostly_fair: "Bastante justas", somewhat_fair: "Um pouco justas", not_very_fair: "Pouco justas", not_fair: "Injustas",
    very_useful: "Muito útil", mostly_useful: "Bastante útil", somewhat_useful: "Um pouco útil", not_very_useful: "Pouco útil", not_useful: "Não foi útil",
    yes_very_clearly: "Sim, muito claramente", mostly: "Bastante", somewhat: "Um pouco", not_really: "Nem tanto",
    yes_definitely: "Sim, definitivamente",
    yes_significantly: "Sim, muito", yes_somewhat: "Sim, um pouco", a_little: "Só um pouco",
    very_easy: "Muito fácil", mostly_easy: "Bastante fácil", somewhat_difficult: "Um pouco difícil", very_difficult: "Muito difícil",
    definitely: "Definitivamente", probably: "Provavelmente", maybe: "Talvez", probably_not: "Provavelmente não", definitely_not: "Definitivamente não",
  },

  promptHint: "Adicione um comentário (opcional).",
  promptStrong: "Conte mais para nós — qual(is) pergunta(s) e por quê.",
  promptStrongFabricated: "Diga-nos qual pergunta e o que foi adicionado.",
  promptStrongIssues: "Descreva o que aconteceu.",
  promptStrongWhyNot: "Diga-nos o que fez você não usar.",
};

const ES = {
  pageTitle: "Feedback beta",
  headerSubtitle: "Ayúdenos a mejorar My English Coach antes del lanzamiento público.",
  progress: (done, total) => `${done} de ${total} secciones completas`,
  submit: "Enviar feedback",
  submitting: "Enviando…",
  back: "Volver a los resultados",
  draftSaved: "Borrador guardado en este dispositivo.",
  draftRestored: "Restauramos su borrador guardado.",
  incompleteToast: "Responda todas las preguntas obligatorias antes de enviar.",
  submitSuccessToast: "¡Gracias por su feedback!",
  submitErrorToast: "Algo salió mal. Inténtelo de nuevo.",
  notEligibleToast: "No puede enviar feedback para esta sesión.",
  alreadySubmittedToast: "Ya envió feedback para esta sesión.",
  optionalLabel: "Opcional",
  requiredLabel: "Obligatoria",
  commentLabel: "Comentario",
  commentRequiredLabel: "Comentario obligatorio",
  commentRequiredHelper: "Explique su respuesta para que podamos mejorar.",
  commentRequiredError: "Añada un comentario antes de enviar.",
  chooseAria: "Elija una opción",

  sectionTitles: {
    overall_experience: "Sección 1 — Experiencia general",
    interview_questions: "Sección 2 — Preguntas de la entrevista",
    assessment_report: "Sección 3 — Informe de evaluación",
    suggested_improved_answers: "Sección 4 — Respuestas mejoradas sugeridas",
    study_guide: "Sección 5 — Guía de estudio",
    re_practice: "Sección 6 — Volver a practicar",
    ease_of_use: "Sección 7 — Facilidad de uso",
    final_feedback: "Sección 8 — Feedback final",
  },

  questions: {
    oe_overall_rating: "¿Cómo calificaría su experiencia general con el Simulador de Entrevistas?",
    oe_best_part: "¿Cuál fue la mejor parte de su experiencia?",
    oe_frustrating: "¿Cuál fue la parte más frustrante o confusa?",
    iq_relevance: "¿Qué tan relevantes fueron las preguntas para su currículum y la descripción del puesto?",
    iq_realistic: "¿Qué tan realistas se sintieron las preguntas en comparación con una entrevista real?",
    iq_hard_wording: "¿Alguna pregunta le resultó difícil de entender por el inglés usado?",
    iq_repetitive: "¿Alguna pregunta le pareció repetitiva o demasiado similar a otra?",
    ar_fair: "¿Qué tan justas le parecieron las puntuaciones según las respuestas que dio?",
    ar_useful: "¿Qué tan útil fue el feedback pregunta por pregunta?",
    ar_clarity: "¿El feedback mostró claramente lo que hizo bien y lo que debe mejorar?",
    ar_incorrect: "¿Algún feedback le pareció incorrecto o basado en algo que usted no dijo?",
    sia_useful: "¿Qué tan útiles fueron las respuestas mejoradas sugeridas?",
    sia_fabricated: "¿Alguna respuesta sugerida incluyó experiencias, responsabilidades, logros o hechos que no son verdaderos sobre usted?",
    sg_reviewed: "¿Revisó la Guía de Estudio?",
    sg_useful: "¿Qué tan útil fue la Guía de Estudio?",
    sg_seven_day_plan: "¿El plan de 7 días le dio actividades claras y prácticas para trabajar?",
    rp_used: "¿Usó la función Volver a Practicar?",
    rp_helped: "¿Repetir las mismas preguntas le ayudó a aplicar el feedback de su primera entrevista?",
    rp_feedback: "¿Qué feedback tiene sobre su experiencia con Volver a Practicar?",
    rp_why_not: "¿Por qué decidió no usar Volver a Practicar?",
    eu_ease: "¿Qué tan fácil fue completar todo el proceso — desde cargar el currículum/descripción hasta revisar sus resultados?",
    eu_issues: "¿Algo no funcionó como esperaba?",
    ff_use_again: "¿Volvería a usar esta herramienta antes de una entrevista real?",
    ff_recommend: "¿La recomendaría a alguien que se prepara para una entrevista en inglés?",
    ff_improve: "¿Qué es lo único que más le gustaría que mejoremos antes del lanzamiento?",
    ff_anything_else: "¿Algo más que le gustaría que sepamos?",
  },

  choiceLabels: {
    excellent: "Excelente", very_good: "Muy buena", good: "Buena", fair: "Regular", poor: "Mala",
    very_relevant: "Muy relevantes", mostly_relevant: "Bastante relevantes", somewhat_relevant: "Algo relevantes", not_very_relevant: "Poco relevantes", not_relevant: "No relevantes",
    very_realistic: "Muy realistas", mostly_realistic: "Bastante realistas", somewhat_realistic: "Algo realistas", not_very_realistic: "Poco realistas", not_realistic: "No realistas",
    yes: "Sí", no: "No", unsure: "No estoy seguro",
    very_fair: "Muy justas", mostly_fair: "Bastante justas", somewhat_fair: "Algo justas", not_very_fair: "Poco justas", not_fair: "Injustas",
    very_useful: "Muy útil", mostly_useful: "Bastante útil", somewhat_useful: "Algo útil", not_very_useful: "Poco útil", not_useful: "Nada útil",
    yes_very_clearly: "Sí, muy claramente", mostly: "Bastante", somewhat: "Algo", not_really: "No mucho",
    yes_definitely: "Sí, definitivamente",
    yes_significantly: "Sí, mucho", yes_somewhat: "Sí, algo", a_little: "Un poco",
    very_easy: "Muy fácil", mostly_easy: "Bastante fácil", somewhat_difficult: "Algo difícil", very_difficult: "Muy difícil",
    definitely: "Definitivamente", probably: "Probablemente", maybe: "Quizás", probably_not: "Probablemente no", definitely_not: "Definitivamente no",
  },

  promptHint: "Añada un comentario (opcional).",
  promptStrong: "Cuéntenos más — qué pregunta(s) y por qué.",
  promptStrongFabricated: "Díganos qué pregunta y qué se añadió.",
  promptStrongIssues: "Describa lo que ocurrió.",
  promptStrongWhyNot: "Díganos qué le impidió usarla.",
};

const FR = {
  pageTitle: "Feedback bêta",
  headerSubtitle: "Aidez-nous à améliorer My English Coach avant le lancement public.",
  progress: (done, total) => `${done} sur ${total} sections complétées`,
  submit: "Envoyer le feedback",
  submitting: "Envoi en cours…",
  back: "Retour aux résultats",
  draftSaved: "Brouillon enregistré sur cet appareil.",
  draftRestored: "Nous avons restauré votre brouillon.",
  incompleteToast: "Veuillez répondre à toutes les questions requises avant d'envoyer.",
  submitSuccessToast: "Merci pour votre feedback !",
  submitErrorToast: "Une erreur est survenue. Veuillez réessayer.",
  notEligibleToast: "Vous ne pouvez pas envoyer de feedback pour cette session.",
  alreadySubmittedToast: "Vous avez déjà envoyé du feedback pour cette session.",
  optionalLabel: "Facultative",
  requiredLabel: "Obligatoire",
  commentLabel: "Commentaire",
  commentRequiredLabel: "Commentaire obligatoire",
  commentRequiredHelper: "Expliquez votre réponse pour que nous puissions améliorer l'outil.",
  commentRequiredError: "Ajoutez un commentaire avant d'envoyer.",
  chooseAria: "Choisissez une option",

  sectionTitles: {
    overall_experience: "Section 1 — Expérience globale",
    interview_questions: "Section 2 — Questions d'entrevue",
    assessment_report: "Section 3 — Rapport d'évaluation",
    suggested_improved_answers: "Section 4 — Réponses améliorées suggérées",
    study_guide: "Section 5 — Guide d'étude",
    re_practice: "Section 6 — Repratiquer",
    ease_of_use: "Section 7 — Facilité d'utilisation",
    final_feedback: "Section 8 — Feedback final",
  },

  questions: {
    oe_overall_rating: "Comment évaluez-vous votre expérience globale avec le Simulateur d'entretien ?",
    oe_best_part: "Quelle a été la meilleure partie de votre expérience ?",
    oe_frustrating: "Quelle a été la partie la plus frustrante ou déroutante ?",
    iq_relevance: "À quel point les questions étaient-elles pertinentes par rapport à votre CV et à la description du poste ?",
    iq_realistic: "À quel point les questions vous ont-elles semblé réalistes comparées à une vraie entrevue ?",
    iq_hard_wording: "Certaines questions étaient-elles difficiles à comprendre à cause de l'anglais utilisé ?",
    iq_repetitive: "Certaines questions vous ont-elles semblé répétitives ou trop similaires ?",
    ar_fair: "À quel point les notes vous ont-elles semblé justes par rapport à vos réponses ?",
    ar_useful: "À quel point le feedback question par question était-il utile ?",
    ar_clarity: "Le feedback a-t-il clairement montré ce que vous avez bien fait et ce que vous devriez améliorer ?",
    ar_incorrect: "Certains commentaires vous ont-ils semblé incorrects ou basés sur quelque chose que vous n'avez pas dit ?",
    sia_useful: "À quel point les réponses améliorées suggérées étaient-elles utiles ?",
    sia_fabricated: "Une réponse suggérée a-t-elle inclus des expériences, responsabilités, réussites ou faits qui n'étaient pas vrais pour vous ?",
    sg_reviewed: "Avez-vous consulté le Guide d'étude ?",
    sg_useful: "À quel point le Guide d'étude était-il utile ?",
    sg_seven_day_plan: "Le plan de 7 jours vous a-t-il donné des activités claires et pratiques à travailler ?",
    rp_used: "Avez-vous utilisé la fonction Repratiquer ?",
    rp_helped: "Répéter les mêmes questions vous a-t-il aidé à appliquer le feedback de votre première entrevue ?",
    rp_feedback: "Quel feedback avez-vous sur votre expérience de Repratiquer ?",
    rp_why_not: "Qu'est-ce qui vous a fait décider de ne pas utiliser Repratiquer ?",
    eu_ease: "À quel point était-il facile de compléter tout le processus — du téléversement du CV/description jusqu'à la révision des résultats ?",
    eu_issues: "Quelque chose n'a-t-il pas fonctionné comme prévu ?",
    ff_use_again: "Utiliseriez-vous à nouveau cet outil avant une vraie entrevue ?",
    ff_recommend: "Le recommanderiez-vous à quelqu'un qui se prépare à une entrevue en anglais ?",
    ff_improve: "Quelle est LA chose que vous aimeriez le plus que nous améliorions avant le lancement ?",
    ff_anything_else: "Autre chose que vous aimeriez nous faire savoir ?",
  },

  choiceLabels: {
    excellent: "Excellente", very_good: "Très bonne", good: "Bonne", fair: "Passable", poor: "Mauvaise",
    very_relevant: "Très pertinentes", mostly_relevant: "Plutôt pertinentes", somewhat_relevant: "Un peu pertinentes", not_very_relevant: "Peu pertinentes", not_relevant: "Pas pertinentes",
    very_realistic: "Très réalistes", mostly_realistic: "Plutôt réalistes", somewhat_realistic: "Un peu réalistes", not_very_realistic: "Peu réalistes", not_realistic: "Pas réalistes",
    yes: "Oui", no: "Non", unsure: "Je ne suis pas certain",
    very_fair: "Très justes", mostly_fair: "Plutôt justes", somewhat_fair: "Un peu justes", not_very_fair: "Peu justes", not_fair: "Injustes",
    very_useful: "Très utile", mostly_useful: "Plutôt utile", somewhat_useful: "Un peu utile", not_very_useful: "Peu utile", not_useful: "Pas utile",
    yes_very_clearly: "Oui, très clairement", mostly: "Plutôt", somewhat: "Un peu", not_really: "Pas vraiment",
    yes_definitely: "Oui, tout à fait",
    yes_significantly: "Oui, beaucoup", yes_somewhat: "Oui, un peu", a_little: "Juste un peu",
    very_easy: "Très facile", mostly_easy: "Plutôt facile", somewhat_difficult: "Un peu difficile", very_difficult: "Très difficile",
    definitely: "Tout à fait", probably: "Probablement", maybe: "Peut-être", probably_not: "Probablement pas", definitely_not: "Pas du tout",
  },

  promptHint: "Ajoutez un commentaire (facultatif).",
  promptStrong: "Dites-nous en plus — quelle(s) question(s) et pourquoi.",
  promptStrongFabricated: "Dites-nous quelle question et ce qui a été ajouté.",
  promptStrongIssues: "Décrivez ce qui s'est passé.",
  promptStrongWhyNot: "Dites-nous ce qui vous a empêché de l'utiliser.",
};

const BUNDLES = { en: EN, "pt-BR": PT, "es-419": ES, "fr-CA": FR };

// Choice-question ids whose "strong prompt" copy differs from the
// default "please tell us which question and why" phrasing.
const CUSTOM_STRONG_PROMPT = {
  sia_fabricated: "promptStrongFabricated",
  ar_incorrect: "promptStrongFabricated",
  eu_issues: "promptStrongIssues",
  rp_why_not: "promptStrongWhyNot",
};

function normalise(lang) {
  if (!lang) return "en";
  const k = String(lang).toLowerCase();
  if (k === "pt-br" || k === "pt" || k === "both") return "pt-BR";
  if (k === "es" || k === "es-419" || k === "es-mx" || k === "es-us") return "es-419";
  if (k === "fr" || k === "fr-ca" || k === "fr-fr") return "fr-CA";
  return "en";
}

export function bfBundle(lang) {
  return BUNDLES[normalise(lang)] || EN;
}

export function strongPromptKey(questionId) {
  return CUSTOM_STRONG_PROMPT[questionId] || "promptStrong";
}
